import { useMemo, useState } from "react";
import MiniViewCard from "./MiniViewCard";
import { jobPnl } from "../utils/margin";

// One QuadrantView per tile. Each renders MiniViewCard with a top-N
// slice from computeDashboardData -- no further data walking here.
// Tiles whose sidebar count is enough on its own (MyTime has its own
// custom component, Margin is a report, Customers/Vendors are mostly
// counters) get lighter views.

const TOP_N = 5;
const MSG_TOP_N = 3;

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const shortDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const STATUS_LABEL = {
  in_progress: "In progress",
  accepted: "Accepted",
  unassigned: "Unassigned",
  scheduled: "Scheduled",
  completed: "Done",
  closed: "Closed",
  canceled: "Canceled",
};

// ------- Customers -------
export function CustomersQV({ data, onClick }) {
  const total = data.totalCustomers;
  const locTotal = data.customers.reduce(
    (s, c) => s + (c.locations?.length || 0),
    0
  );
  return (
    <MiniViewCard
      title="Customers"
      badge={`${total} total`}
      onClick={onClick}
      footer="Open list →"
    >
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "8px 0",
        }}
      >
        <div style={{ fontSize: "36px", fontWeight: 700 }}>{total}</div>
        <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
          across {locTotal} location{locTotal === 1 ? "" : "s"}
        </div>
      </div>
    </MiniViewCard>
  );
}

// ------- Jobs -------
export function JobsQV({ data, onClick }) {
  const rows = data.activeJobsSorted.slice(0, TOP_N).map((j) => {
    const cust = data.customerById[j.customerId];
    const status = j.status || "unassigned";
    return {
      label: cust?.name || "(unknown)",
      sub: j.title || j.scope || "",
      right: STATUS_LABEL[status] || status,
    };
  });
  return (
    <MiniViewCard
      title="Jobs"
      badge={`${data.activeJobsSorted.length} active`}
      rows={rows}
      empty="No active jobs"
      onClick={onClick}
    />
  );
}

// ------- Service Tickets -------
export function ServiceTicketsQV({ data, onClick }) {
  const rows = data.unackedTickets.slice(0, TOP_N).map((t) => {
    const cust = data.customerById[t.customerId];
    const loc = cust?.locations?.find((l) => l.id === t.locationId);
    return {
      label: cust?.name || "(unknown)",
      sub: loc?.name || "",
      right: shortDate(t.createdAt),
    };
  });
  return (
    <MiniViewCard
      title="Service Tickets"
      badge={`${data.unackedTickets.length} unacked`}
      rows={rows}
      empty="No unacked tickets"
      onClick={onClick}
    />
  );
}

// ------- Quotes -------
export function QuotesQV({ data, onClick }) {
  const rows = data.openQuotes.slice(0, TOP_N).map((q) => {
    const cust = data.customerById[q.customerId];
    return {
      label: cust?.name || "(unknown)",
      sub: q.title || q.quoteNumber || "",
      right: q.totalAmount ? money(q.totalAmount) : "",
    };
  });
  return (
    <MiniViewCard
      title="Quotes"
      badge={`${data.openQuotes.length} open`}
      rows={rows}
      empty="No open quotes"
      onClick={onClick}
    />
  );
}

// ------- Closed Jobs (awaiting bill) -------
// Each row carries the job in row.data; onRowClick wires through to
// App.js's setSelectedJob, jumping straight into JobDetailPage /
// JobCosting so billing can finalize the bill without bouncing
// through the Closed Jobs list page first.
export function ClosedJobsQV({ data, onClick, props }) {
  const onOpenJob = props?.onOpenJob;
  const rows = data.closedJobsAwaitingBill.slice(0, TOP_N).map((j) => {
    const cust = data.customerById[j.customerId];
    return {
      label: cust?.name || "(unknown)",
      sub: j.title || j.jobNumber || "",
      right: shortDate(j.closedAt || j.updatedAt),
      data: j,
    };
  });
  return (
    <MiniViewCard
      title="Closed Jobs"
      badge={
        data.closedJobsAwaitingBill.length > 0
          ? `${data.closedJobsAwaitingBill.length} awaiting bill`
          : null
      }
      rows={rows}
      empty="Inbox zero — nothing to bill"
      onClick={onClick}
      onRowClick={onOpenJob ? (job) => onOpenJob(job) : undefined}
    />
  );
}

// ------- Invoices -------
export function InvoicesQV({ data, onClick }) {
  const rows = data.recentInvoices.slice(0, TOP_N).map((row) => {
    const cust = data.customerById[row.job.customerId];
    return {
      label: cust?.name || "(unknown)",
      sub: row.invoiceNumber || row.job.title || "",
      right: money(row.total),
    };
  });
  return (
    <MiniViewCard
      title="Invoices"
      badge={`${data.recentInvoices.length} issued`}
      rows={rows}
      empty="No invoices yet"
      onClick={onClick}
    />
  );
}

// ------- Contracts -------
export function ContractsQV({ data, onClick }) {
  const rows = data.activeContracts.slice(0, TOP_N).map((c) => {
    const cust = data.customerById[c.customerId];
    const freq =
      c.visitFrequency ||
      (c.frequencyMonths
        ? `Every ${c.frequencyMonths}mo`
        : "");
    return {
      label: cust?.name || "(unknown)",
      sub: c.title || "",
      right: freq,
    };
  });
  return (
    <MiniViewCard
      title="Contracts"
      badge={`${data.activeContracts.length} active`}
      rows={rows}
      empty="No active contracts"
      onClick={onClick}
    />
  );
}

// ------- Purchase Orders -------
export function POsQV({ data, onClick }) {
  const rows = data.openPOs.slice(0, TOP_N).map((p) => {
    const vendor =
      p.vendorName ||
      data.vendorById[p.vendorId]?.name ||
      "(no vendor)";
    return {
      label: vendor,
      sub: p.title || p.poNumber || "",
      right: p.expectedArrival
        ? shortDate(p.expectedArrival)
        : p.total
        ? money(p.total)
        : "",
    };
  });
  return (
    <MiniViewCard
      title="Purchase Orders"
      badge={`${data.openPOs.length} open`}
      rows={rows}
      empty="No open POs"
      onClick={onClick}
    />
  );
}

// ------- Vendors -------
export function VendorsQV({ data, onClick }) {
  const rows = data.activeVendors.slice(0, TOP_N).map((v) => ({
    label: v.name || "(unnamed)",
    sub:
      [v.city, v.state].filter(Boolean).join(", ") ||
      v.email ||
      v.phone ||
      "",
    right: "",
  }));
  return (
    <MiniViewCard
      title="Vendors"
      badge={`${data.activeVendors.length} active`}
      rows={rows}
      empty="No active vendors"
      onClick={onClick}
    />
  );
}

// ------- Margin Report -------
// 30 / 60 / 90 day rollup of revenue, cost, and margin across jobs
// finalized in the period. Uses jobPnl per job (cost basis + non-voided
// bill revenue). Toggle buttons stopPropagation so they don't trigger
// the card's navigate-to-MarginReportPage click.
export function MarginQV({ data, onClick }) {
  const [period, setPeriod] = useState(30);

  const totals = useMemo(() => {
    const cutoffMs = Date.now() - period * 86400000;
    let revenue = 0;
    let cost = 0;
    let margin = 0;
    let jobs = 0;
    for (const j of data.jobsRaw) {
      const finalizedAt =
        j.bills && j.bills.length > 0
          ? j.bills[j.bills.length - 1].finalizedAt
          : j.billFinalizedAt;
      if (!finalizedAt) continue;
      if (new Date(finalizedAt).getTime() < cutoffMs) continue;
      const p = jobPnl(j);
      revenue += p.revenue;
      cost += p.totalCost;
      margin += p.margin;
      jobs += 1;
    }
    const pct = revenue > 0 ? (margin / revenue) * 100 : 0;
    return { revenue, cost, margin, pct, jobs };
  }, [period, data.jobsRaw]);

  const periodToggle = (
    <div style={{ display: "flex", gap: "4px" }}>
      {[30, 60, 90].map((p) => {
        const active = period === p;
        return (
          <button
            key={p}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setPeriod(p);
            }}
            style={{
              padding: "2px 7px",
              fontSize: "10px",
              fontWeight: 700,
              border: `1px solid ${active ? "#1a5fb4" : "#d0d4d9"}`,
              background: active ? "#1a5fb4" : "white",
              color: active ? "white" : "#5a6470",
              borderRadius: "4px",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {p}d
          </button>
        );
      })}
    </div>
  );

  const Stat = ({ label, value, color, bold }) => (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        padding: "4px 0",
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <span style={{ fontSize: "12px", color: "#666" }}>{label}</span>
      <span
        style={{
          fontSize: bold ? "14px" : "13px",
          fontWeight: bold ? 700 : 600,
          color: color || "#222",
        }}
      >
        {value}
      </span>
    </div>
  );

  return (
    <MiniViewCard
      title="Margin"
      badge={periodToggle}
      onClick={onClick}
      footer="Open report →"
    >
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "4px 0",
        }}
      >
        <Stat label="Revenue" value={money(totals.revenue)} />
        <Stat label="Cost" value={money(totals.cost)} />
        <Stat
          label="Margin"
          value={`${money(totals.margin)} · ${totals.pct.toFixed(1)}%`}
          bold
          color={totals.margin >= 0 ? "#1a7f37" : "#c12c2c"}
        />
        <div
          style={{
            fontSize: "11px",
            color: "#888",
            marginTop: "6px",
          }}
        >
          across {totals.jobs} job{totals.jobs === 1 ? "" : "s"} finalized
          in last {period} days
        </div>
      </div>
    </MiniViewCard>
  );
}

// ------- Messages -------
export function MessagesQV({ data, onClick }) {
  const rows = data.unreadMessages.slice(0, MSG_TOP_N).map((n) => ({
    label:
      n.fromName || n.senderName || n.fromEmail || "(unknown sender)",
    sub: (n.body || n.preview || n.text || n.title || "").slice(0, 80),
    right: shortDate(n.createdAt),
  }));
  return (
    <MiniViewCard
      title="Messages"
      badge={
        data.unreadMessageCount > 0
          ? `${data.unreadMessageCount} unread`
          : null
      }
      rows={rows}
      empty="No unread messages"
      onClick={onClick}
    />
  );
}

// ------- Recent Activity -------
export function RecentActivityQV({ data, onClick }) {
  const rows = data.recentRecords.slice(0, TOP_N).map((r) => ({
    label: r.equipment.type || "(equipment)",
    sub: `${r.customer.name || ""} · ${r.location.name || ""}`,
    right: shortDate(r.record.createdAt || r.record.date),
  }));
  return (
    <MiniViewCard
      title="Recent Activity"
      badge={`${data.recentRecords.length} in 7 days`}
      rows={rows}
      empty="No service records this week"
      onClick={onClick}
    />
  );
}

// ------- PM Due Soon -------
export function PMDueSoonQV({ data, onClick }) {
  const rows = data.pmDueSoonList.slice(0, TOP_N).map(({ eq, pm }) => ({
    label: eq.type || "(equipment)",
    sub: `${eq.customerName || ""} · ${eq.locationName || ""}`,
    right:
      pm.diffDays === 0
        ? "today"
        : `${pm.diffDays}d`,
    rightColor: pm.diffDays <= 7 ? "#d97706" : "#444",
  }));
  return (
    <MiniViewCard
      title="PM Due Soon"
      badge={`${data.pmDueSoonList.length} within 30d`}
      rows={rows}
      empty="All PM up to date"
      onClick={onClick}
    />
  );
}

// ------- PM Overdue -------
export function PMOverdueQV({ data, onClick }) {
  const rows = data.pmOverdueList.slice(0, TOP_N).map(({ eq, pm }) => ({
    label: eq.type || "(equipment)",
    sub: `${eq.customerName || ""} · ${eq.locationName || ""}`,
    right: `${Math.abs(pm.diffDays)}d late`,
    rightColor: "#c12c2c",
  }));
  return (
    <MiniViewCard
      title="PM Overdue"
      badge={
        data.pmOverdueList.length > 0
          ? `${data.pmOverdueList.length} overdue`
          : null
      }
      rows={rows}
      empty="No PM overdue"
      onClick={onClick}
    />
  );
}
